// Throughput micro-benchmarks for S-01/S-03/S-04/S-08: raw decode vs decode + copyTo vs decode + GPU upload.

import { ALL_FORMATS, BlobSource, EncodedPacketSink, Input } from 'mediabunny';

export interface BenchResult {
  mode: string;
  frames: number;
  ms: number;
  fps: number;
  copyMsAvg?: number;
  format?: string | null;
}

export async function benchDecode(file: File, mode: 'decode' | 'copy' | 'texture', maxFrames = 240): Promise<BenchResult> {
  const input = new Input({ formats: ALL_FORMATS, source: new BlobSource(file) });
  const track = (await input.getPrimaryVideoTrack())!;
  const config = (await track.getDecoderConfig())!;
  const sink = new EncodedPacketSink(track);
  let gl: WebGL2RenderingContext | null = null;
  let tex: WebGLTexture | null;
  if (mode === 'texture') {
    const c = new OffscreenCanvas(16, 16);
    gl = c.getContext('webgl2')!;
    tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, tex);
  }
  let frames = 0;
  let copyMs = 0;
  let format: string | null = null;
  const pending: Promise<void>[] = [];
  const dec = new VideoDecoder({
    output: (f) => {
      frames++;
      format = f.format;
      if (mode === 'copy' && f.format) {
        const t0 = performance.now();
        const buf = new ArrayBuffer(f.allocationSize());
        pending.push(
          f.copyTo(buf).then(() => {
            copyMs += performance.now() - t0;
            f.close();
          }),
        );
      } else if (mode === 'texture' && gl) {
        const t0 = performance.now();
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, gl.RGBA, gl.UNSIGNED_BYTE, f);
        gl.finish();
        copyMs += performance.now() - t0;
        f.close();
      } else f.close();
    },
    error: () => undefined,
  });
  dec.configure({ ...config, optimizeForLatency: true });
  const t0 = performance.now();
  let n = 0;
  for await (const p of sink.packets()) {
    while (dec.decodeQueueSize > 8) await new Promise((r) => setTimeout(r, 1));
    dec.decode(p.toEncodedVideoChunk());
    if (++n >= maxFrames) break;
  }
  await dec.flush();
  await Promise.all(pending);
  const ms = performance.now() - t0;
  dec.close();
  input.dispose();
  return { mode, frames, ms: Math.round(ms), fps: Math.round((frames / ms) * 10000) / 10, copyMsAvg: frames ? Math.round((copyMs / frames) * 100) / 100 : 0, format };
}
